import { ToggleOn, ToggleOff, ViewList, GridView } from "@mui/icons-material";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
  Paper,
  Box,
  Typography,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useToast } from "../../../../Hooks/toast";
import useCallAxios from "../../../../Hooks/useCallAxios";
import CustomButton from "../CustomButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import api from "../../../../Services/api";
import Tooltip from "../../../../Components/Atoms/Tooltip";
import { ConfirmDialogModal } from "../../../../Components/Atoms";
import { loadItem, saveItem } from "../../../../Utils";

interface SubaccountApiKeyResponse {
  id: string
  active: boolean
  apikey: string
  apisecret: string
  created_at: string
  password: string
  username: string
}

interface ApiTableListProps {
  handleCopy: (value: string) => void;
  subAccountId: string;
}

type ViewMode = 'list' | 'grid';

const ApiTableList = ({ handleCopy, subAccountId }: ApiTableListProps) => {

  const [apiKeys, setApiKeys] = useState<SubaccountApiKeyResponse[]>([]);
  const [openCreateConfirm, setOpenCreateConfirm] = useState(false);
  const [openToggleConfirm, setOpenToggleConfirm] = useState(false);
  const [selectedKey, setSelectedKey] = useState<SubaccountApiKeyResponse | null>(null);
  const viewModeKey = '@ApiKeysTable:viewMode';
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = loadItem(viewModeKey);
    return stored === 'list' || stored === 'grid' ? stored : 'list';
  });

  const { addToast } = useToast();

  const getAll = async () => {
    const res = await api.get(`/subaccount/${subAccountId}/api`);
    setApiKeys(res.data);
  }

  useEffect(() => {
    getAll();
  }, [subAccountId])

  useEffect(() => {
    saveItem(viewMode, viewModeKey);
  }, [viewMode]);

  const handleToggle = async (apiKey: SubaccountApiKeyResponse) => {
    await api.patch(`/subaccount/${subAccountId}/api/toggle/${apiKey.id}`);
    await getAll();

    addToast({
      title: 'Sucesso',
      type: 'success',
      description: `API ${apiKey.active ? 'desativada' : 'ativada'} com sucesso`
    });
  }

  const createApi = async () => {
    await api.post(`/subaccount/${subAccountId}/api`, { afterId: "api" });
    await getAll();

    addToast({
      title: 'Sucesso',
      type: 'success',
      description: 'API criada com sucesso'
    });
  }

  return (
    <>
      <CustomButton
        fullWidth
        onClick={() => setOpenCreateConfirm(true)}
        tooltip="Criar nova chave de API"
      >
        Criar uma nova chave
      </CustomButton>
      <Box mt={2} mb={1} display="flex" justifyContent="flex-end">
        <ToggleButtonGroup
          size="small"
          color="primary"
          value={viewMode}
          exclusive
          onChange={(_, value) => value && setViewMode(value)}
          aria-label="modo de visualização"
        >
          <Tooltip title="Visualização em Lista">
            <ToggleButton value="list" aria-label="lista">
              <ViewList fontSize="small" />
              <Box component="span" ml={0.5}>Lista</Box>
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Visualização em Grade">
            <ToggleButton value="grid" aria-label="grade">
              <GridView fontSize="small" />
              <Box component="span" ml={0.5}>Grade</Box>
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>
      <TableContainer component={Paper} sx={{ whiteSpace: viewMode === 'list' ? 'nowrap' : 'normal' }}>
        {viewMode === 'list' ? (
          <Table
            size="small"
            aria-label="tabela de chaves de API em lista"
            sx={{
              '& .MuiTableCell-root': { py: 0.5, px: 1 },
              '& .MuiTableRow-root': { height: 36 },
              '& .MuiIconButton-root': { p: 0.5 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>API key</TableCell>
                <TableCell>API Secret</TableCell>
                <TableCell>Nome de usuário</TableCell>
                <TableCell>Senha</TableCell>
                <TableCell>Data criação</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.length === 0 && (
                <TableRow>
                  <TableCell align="left" width={10} colSpan={10}>
                    Nenhuma chave foi adicionada
                  </TableCell>
                </TableRow>
              )}
              {apiKeys.map((row) => (
                <TableRow key={row.apikey}>
                  <TableCell align="left" width={10}>
                    <Tooltip title={row.active ? 'Desativar chave' : 'Ativar chave'}>
                      <IconButton
                        color={row.active ? 'success' : 'default'}
                        size="large"
                        onClick={() => { setSelectedKey(row); setOpenToggleConfirm(true); }}
                      >
                        {row.active ? <ToggleOff fontSize="large" /> : <ToggleOn fontSize="large" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Typography fontFamily="monospace !important" fontSize="14px">
                      {row.apikey}
                      <IconButton onClick={() => handleCopy(row.apikey)} disabled={!row.apikey}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontFamily="monospace !important" fontSize="14px">
                      {row.apisecret}
                      <IconButton onClick={() => handleCopy(row.apisecret)} disabled={!row.apisecret}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Typography>
                  </TableCell>
                  <TableCell>{row.username}</TableCell>
                  <TableCell>{row.password}</TableCell>
                  <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    {row.active ? (
                      <Chip label="Ativo" color="success" />
                    ) : (
                      <Chip label="Inativo" color="error" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table
            size="small"
            aria-label="tabela de chaves de API em grade"
            sx={{
              '& .MuiTableCell-root': { py: 1.25, px: 1.5, borderBottom: 'none' },
              '& .MuiIconButton-root': { p: 0.5 },
              borderCollapse: 'separate',
              borderSpacing: '0 8px',
            }}
          >
            <TableBody>
              {apiKeys.length === 0 && (
                <TableRow>
                  <TableCell align="left">Nenhuma chave foi adicionada</TableCell>
                </TableRow>
              )}
              {apiKeys.map((row) => (
                <TableRow key={row.apikey}>
                  <TableCell>
                    <Box sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="subtitle2">Chave de API</Typography>
                      <Tooltip title={row.active ? 'Desativar chave' : 'Ativar chave'}>
                        <IconButton
                          color={row.active ? 'success' : 'default'}
                          size="medium"
                          sx={{ width: 44, height: 44 }}
                          onClick={() => { setSelectedKey(row); setOpenToggleConfirm(true); }}
                        >
                          {row.active ? <ToggleOff fontSize="large" /> : <ToggleOn fontSize="large" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography fontFamily="monospace !important" fontSize="14px" display="flex" alignItems="center">
                      {row.apikey}
                      <IconButton onClick={() => handleCopy(row.apikey)} disabled={!row.apikey}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Typography>
                    <Box mt={0.75}>
                      <Typography variant="subtitle2">API Secret</Typography>
                      <Typography fontFamily="monospace !important" fontSize="14px" display="flex" alignItems="center">
                        {row.apisecret}
                        <IconButton onClick={() => handleCopy(row.apisecret)} disabled={!row.apisecret}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Typography>
                    </Box>
                    <Box mt={0.75}>
                      <Typography variant="subtitle2">Nome de usuário</Typography>
                      <Typography fontSize="14px">{row.username}</Typography>
                    </Box>
                    <Box mt={0.75}>
                      <Typography variant="subtitle2">Senha</Typography>
                      <Typography fontSize="14px">{row.password}</Typography>
                    </Box>
                    <Box mt={0.75} display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle2">Data criação</Typography>
                        <Typography fontSize="14px">{new Date(row.created_at).toLocaleDateString()}</Typography>
                      </Box>
                      <Box>
                        {row.active ? (
                          <Chip label="Ativo" color="success" />
                        ) : (
                          <Chip label="Inativo" color="error" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <ConfirmDialogModal
        open={openCreateConfirm}
        onClose={() => setOpenCreateConfirm(false)}
        text={"Deseja criar uma nova chave de API?"}
        description={"Para criar clique em confirmar."}
        refreshAction={async (ok: boolean) => {
          setOpenCreateConfirm(false);
          if (ok) await createApi();
        }}
      />
      <ConfirmDialogModal
        open={openToggleConfirm}
        onClose={() => { setOpenToggleConfirm(false); setSelectedKey(null); }}
        text={selectedKey?.active ? "Deseja desativar esta chave de API?" : "Deseja ativar esta chave de API?"}
        description={selectedKey?.active ? "A chave deixará de funcionar imediatamente." : "A chave ficará ativa para uso."}
        refreshAction={async (ok: boolean) => {
          const key = selectedKey;
          setOpenToggleConfirm(false);
          setSelectedKey(null);
          if (ok && key) await handleToggle(key);
        }}
      />
    </>
  );
};

export default ApiTableList;
